import { PagingParameters } from "@/api/dtos";
import { readTablePreferences } from "@/hooks/useTablePreferences";
import {
  useCountRelatedFields,
  useCreateRelatedFields,
  useDeleteRelation,
  useGetNotRelatedResource,
  useGetRelatedFields,
} from "@/api/related-fields";
import { generateTable } from "@/config/dashboard/general";
import {
  photographiesColumns,
  photographiesColumnsVisibility,
} from "@/config/dashboard/photographies";
import { Photography } from "@/lib/types";
import { ColumnDef, VisibilityState } from "@tanstack/react-table";
import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { RelatedRecordRowActions } from "@/components/table/RelatedRecordRowActions";
import { createActionsColumnDef } from "@/components/table/table-actions-column";
import ShowElements from "../drawer/ShowElements";
import { LinkableType } from "../LinksManagement";
import { DataTable } from "../ui/datatable";
import { Input } from "../ui/input";
import { RelatedLinksListGridLayout } from "./RelatedLinksListGridLayout";
import { GridCardWithActions } from "./GridCardWithActions";
import { PhotographyLinkGridCard } from "./relatedLinkGridCards";

interface LinksProps {
  id: string;
  type: LinkableType;
}

/**
 * ArtworkLinks component
 * @returns {JSX.Element}
 */
const PhotographiesLinks = ({ id, type }: LinksProps) => {
  const { t } = useTranslation();

  const [getPaginationRelatedPhotographies, setPaginationRelatedPhotographies] =
    useState<PagingParameters>({
      page: 0,
      pageSize: 10,
    });

  const { data: relatedFields } = useGetRelatedFields(
    id,
    type,
    "relatedPhotography",
    getPaginationRelatedPhotographies.pageSize,
    getPaginationRelatedPhotographies.page *
      getPaginationRelatedPhotographies.pageSize,
  );

  const { data: countRelatedPhotographies } = useCountRelatedFields(id, type, [
    "relatedPhotography",
  ]);

  const { mutate: createRelatedFields } = useCreateRelatedFields(
    id,
    type,
    "relatedPhotography",
  );
  const { mutate: deleteRelation } = useDeleteRelation(
    id,
    type,
    "relatedPhotography",
  );

  const [searchContent, setSearchContent] = useState("");

  const handleAddMode = () => {
    setIsAddMode(!isAddMode);
  };

  const [isAddMode, setIsAddMode] = useState(false);
  const [selectedPhotographyId, setSelectedPhotographyId] = useState<
    string | null
  >(null);

  const [getPagination, setPagination] = useState<PagingParameters>({
    page: 0,
    pageSize: 10,
  });

  const { data: notRelatedPhotographies } =
    useGetNotRelatedResource<Photography>({
      resultType: "photography",
      resourceId: id,
      pagination: getPagination,
      searchQuery: searchContent,
    });

  const handleCreateRelation = useCallback(
    (idToLink: string) => {
      createRelatedFields([id, idToLink]);
    },
    [createRelatedFields, id],
  );

  const handleDeleteRelation = useCallback(
    (photographyId: string) => {
      deleteRelation(photographyId);
    },
    [deleteRelation],
  );

  const baseColumns = useMemo(
    () => generateTable(photographiesColumns, "photography", t),
    [t],
  );

  const renderPhotographyRowActions = useCallback(
    (item: Photography) => (
      <RelatedRecordRowActions
          isAddMode={isAddMode}
          resourceId={item.id}
          previewType="photography"
          previewElements={[
            {
              label: t("photosDescriptionNumeroEnregistrement"),
              value: item.photosDescriptionNumeroEnregistrement,
              type: "text",
            },
            {
              label: t("photosDescriptionLegendeRedigee"),
              value: item.photosDescriptionLegendeRedigee,
              type: "richtext",
            },
            {
              label: t("attachments"),
              value: item.attachments,
              type: "image",
            },
          ]}
          onDelete={() => handleDeleteRelation(item.id)}
          onAdd={() => handleCreateRelation(item.id)}
        />
    ),
    [handleCreateRelation, handleDeleteRelation, isAddMode, t],
  );

  const columns: ColumnDef<Photography, any>[] = useMemo(() => {
    return [
      ...baseColumns,
      createActionsColumnDef<Photography>(({ row }) =>
        renderPhotographyRowActions(row.original),
      ),
    ];
  }, [baseColumns, renderPhotographyRowActions]);

  const STORAGE_KEY = "photography-links-table";
  const defaultVisibility = {
    ...photographiesColumnsVisibility,
    actions: true,
  };
  const [columnsOrder, setColumnsOrder] = useState<string[]>(() => {
    try {
      const saved = readTablePreferences(STORAGE_KEY);
      if (saved) {
        const { order } = saved;
        return order || [];
      }
    } catch {
      // ignore
    }
    return [];
  });
  const [columnVisibility, setColumnVisibility] = useState<VisibilityState>(
    () => {
      try {
        const saved = readTablePreferences(STORAGE_KEY);
        if (saved) {
          const { visibility } = saved;
          if (visibility && typeof visibility === "object")
            return { ...defaultVisibility, ...visibility };
        }
      } catch {
        // ignore
      }
      return { ...defaultVisibility };
    },
  );

  const tableOptions = {
    enableColumnResizing: true,
    onColumnVisibilityChange: setColumnVisibility,
    onColumnOrderChange: setColumnsOrder,
    state: { columnVisibility, columnOrder: columnsOrder },
  };

  const relatedPaginationProps = {
    pagination: getPaginationRelatedPhotographies,
    setPage: setPaginationRelatedPhotographies,
    totalSize: countRelatedPhotographies?.relatedPhotography ?? 0,
  };

  return (
    <RelatedLinksListGridLayout
      storageKey={STORAGE_KEY}
      title={t("linkedPhotographies")}
      isAddMode={isAddMode}
      onBack={handleAddMode}
      onToggleAdd={handleAddMode}
      searchInput={
        <div className="flex items-center gap-2">
          <Input
            placeholder={t("search")}
            className="w-80"
            value={searchContent}
            onChange={(e) => setSearchContent(e.target.value)}
          />
        </div>
      }
      addModeTable={
        <DataTable
          columns={columns}
          data={notRelatedPhotographies?.data ?? []}
          pagination={{
            pagination: getPagination,
            setPage: setPagination,
            totalSize: notRelatedPhotographies?.totalSize ?? 0,
          }}
          storageKey={STORAGE_KEY}
          options={tableOptions}
        />
      }
      listTable={
        <DataTable
          columns={columns}
          data={relatedFields ?? []}
          pagination={relatedPaginationProps}
          storageKey={STORAGE_KEY}
          options={tableOptions}
        />
      }
      gridContent={
        <>
          {relatedFields?.map((row: Photography) => (
            <div key={row.id}>
              <GridCardWithActions actions={renderPhotographyRowActions(row)}>
                <PhotographyLinkGridCard
                item={row}
                t={t}
                onTextClick={() => setSelectedPhotographyId(row.id)}
              />
              </GridCardWithActions>
              <ShowElements
                type="photography"
                id={row.id}
                open={selectedPhotographyId === row.id}
                onOpenChange={(open) => {
                  if (!open) {
                    setSelectedPhotographyId(null);
                  }
                }}
                trigger={<span />}
                elements={[
                  {
                    label: t("photosDescriptionNumeroEnregistrement"),
                    value: row.photosDescriptionNumeroEnregistrement,
                    type: "text",
                  },
                  {
                    label: t("photosDescriptionLegendeRedigee"),
                    value: row.photosDescriptionLegendeRedigee ?? "",
                    type: "richtext",
                  },
                  {
                    label: t("attachments"),
                    value: row.attachments,
                    type: "image",
                  },
                ]}
              />
            </div>
          ))}
        </>
      }
      relatedPagination={relatedPaginationProps}
    />
  );
};

export default PhotographiesLinks;
